import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Modal, Pagination, Image } from "antd";
import { companyID } from "../../../auth";
import api from "../../../api/axios";
import { useLocation, useSearchParams } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import config from "../../../config";

/* ─── helpers ───────────────────────────────────────────── */
const convertToIndianTime = (epochTime) => {
  const date = new Date(epochTime * 1000);
  return date.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
};

const truncateStatus = (text = "", max = 38) =>
  text.length > max ? text.substring(0, max) + "…" : text;

/* ─── CheckType badge ───────────────────────────────────── */
const CheckTypeBadge = ({ type }) => {
  if (!type) return <span style={{ color: "#9ca3af" }}>—</span>;
  const isIn = type === "CHECK_IN";
  const label = isIn ? "Check In" : "Check Out";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 600,
        border: `1.5px solid ${isIn ? "#22c55e" : "#f97316"}`,
        color: isIn ? "#16a34a" : "#ea6c00",
        background: isIn ? "#f0fdf4" : "#fff7ed",
        whiteSpace: "nowrap",
        letterSpacing: "0.01em",
      }}
    >
      {label}
    </span>
  );
};

/* ════════════════════════════════════════════════════════ */
const EmployeeWorkStatusPage = () => {
  const [searchParams] = useSearchParams();
  const employee_id = searchParams.get("employee_id");
  const location = useLocation();
  const { employeeName = "" } = location.state || {};
  const company_id = companyID();
  const loginEmployeeId = localStorage.getItem("employeeID");

  const [employees, setEmployees] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isFullStatusModalVisible, setIsFullStatusModalVisible] = useState(false);
  const [workStatus, setWorkStatus] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [workStatusPage, setWorkStatusPage] = useState(1);
  const [workStatusPageSize, setWorkStatusPageSize] = useState(5);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await api.get(
          `employees/employeeDetails/${company_id}?status=1`
        );
        setEmployees(
          response?.data?.filter((item) =>
            employee_id ? item.id == employee_id : item.id != loginEmployeeId
          )
        );
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };
    if (company_id) fetchEmployees();
  }, []);

  const handleEmployeeClick = async (employee) => {
    setSelectedEmployee(employee);
    setWorkStatus([]);
    setWorkStatusPage(1);
    setIsModalVisible(true);
    try {
      const response = await api.get(`employee/${employee.id}/work-status`);
      setWorkStatus(response.data);
    } catch (error) {
      console.error("Error fetching work status:", error);
    }
  };

  const handleCancel = () => setIsModalVisible(false);

  const handleFullStatusClose = () => {
    setIsFullStatusModalVisible(false);
    setIsModalVisible(true);
  };

  const handleViewMore = (fullStatus) => {
    setSelectedStatus(fullStatus);
    setIsModalVisible(false);
    setIsFullStatusModalVisible(true);
  };

  /* pagination slice */
  const pagedStatus = workStatus.slice(
    (workStatusPage - 1) * workStatusPageSize,
    workStatusPage * workStatusPageSize
  );

  const startIndex = (currentPage - 1) * pageSize;
  const currentEmployees = employees.slice(startIndex, startIndex + pageSize);

  return (
    <div className="page-wrapper">
      <div className="content container-fluid position-relative">
        <Breadcrumbs
          maintitle={`${employeeName} Work Status`}
          title="Dashboard"
          subtitle="Work Status"
        />

        <div className="row">
          {currentEmployees.map((employee) => (
            <div
              className="col-md-4 col-xs-12 col-sm-6 col-lg-3 mb-4"
              key={employee.id}
            >
              <div
                className="card h-80"
                onClick={() => handleEmployeeClick(employee)}
                style={{ cursor: "pointer" }}
              >
                <div className="card-body text-center">
                  <h5 className="card-title">
                    {employee.first_name} {employee.last_name}
                  </h5>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="d-flex justify-content-center mb-4">
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={employees.length}
            showSizeChanger
            onChange={(p, ps) => {
              setCurrentPage(p);
              setPageSize(ps);
            }}
            pageSizeOptions={["5", "10", "15", "20"]}
          />
        </div>

        {/* ══ Main Work Status Modal ══ */}
        <Modal
          open={isModalVisible}
          onCancel={handleCancel}
          width={700}
          title={
            <span style={{ fontSize: 16, fontWeight: 600, color: "#111827" }}>
              Work Status of {selectedEmployee?.first_name}{" "}
              {selectedEmployee?.last_name}
            </span>
          }
          footer={
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {/* pagination — bottom left */}
              <Pagination
                current={workStatusPage}
                pageSize={workStatusPageSize}
                total={workStatus.length}
                showSizeChanger
                onChange={(p, ps) => {
                  setWorkStatusPage(p);
                  setWorkStatusPageSize(ps);
                }}
                pageSizeOptions={["5", "10", "15"]}
                size="small"
              />

              {/* close button — bottom right */}
              <button
                className="btn btn-primary"
                onClick={handleCancel}
              >
                Close
              </button>
            </div>
          }
          styles={{ body: { padding: "0" } }}
        >
          {/* ── table ── */}
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 14,
                color: "#374151",
              }}
            >
              <thead>
                <tr
                  style={{
                    borderBottom: "2px solid #e5e7eb",
                    background: "#fff",
                  }}
                >
                  {["#", "Work Status", "Check Type", "Submitted At", "Photo"].map(
                    (h) => (
                      <th
                        key={h}
                        style={{
                          padding: "12px 16px",
                          textAlign: "left",
                          fontWeight: 700,
                          fontSize: 13,
                          color: "#111827",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>

              <tbody>
                {workStatus.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      style={{
                        textAlign: "center",
                        padding: "40px 16px",
                        color: "#9ca3af",
                      }}
                    >
                      No work status available for this employee.
                    </td>
                  </tr>
                ) : (
                  pagedStatus.map((record, idx) => {
                    const globalIdx =
                      (workStatusPage - 1) * workStatusPageSize + idx + 1;
                    const isLong = (record.work_status || "").length > 38;

                    return (
                      <tr
                        key={idx}
                        style={{
                          borderBottom: "1px solid #f3f4f6",
                          background: "#fff",
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "#f9fafb")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "#fff")
                        }
                      >
                        {/* # */}
                        <td
                          style={{
                            padding: "14px 16px",
                            color: "#6b7280",
                            fontWeight: 500,
                            width: 40,
                          }}
                        >
                          {globalIdx}
                        </td>

                        {/* Work Status */}
                        <td style={{ padding: "14px 16px" }}>
                          {truncateStatus(record.work_status || "")}
                          {isLong && (
                            <span
                              style={{
                                color: "#2563eb",
                                cursor: "pointer",
                                fontSize: 12,
                                marginLeft: 4,
                                fontWeight: 500,
                              }}
                              onClick={() =>
                                handleViewMore(record.work_status)
                              }
                            >
                              more
                            </span>
                          )}
                        </td>

                        {/* Check Type */}
                        <td style={{ padding: "14px 16px", whiteSpace: "nowrap" }}>
                          <CheckTypeBadge type={record.check_type} />
                        </td>

                        {/* Submitted At */}
                        <td
                          style={{
                            padding: "14px 16px",
                            whiteSpace: "nowrap",
                            color: "#374151",
                          }}
                        >
                          {convertToIndianTime(record.submitted_at)}
                        </td>

                        {/* Photo — only if image_url is not null */}
                        <td style={{ padding: "14px 16px" }}>
                          {record.image_url ? (
                            <Image
                              src={`${config.JAVA_API_BASE_URL}${record.image_url}`}
                              width={52}
                              height={40}
                              style={{
                                objectFit: "cover",
                                borderRadius: 4,
                                border: "1px solid #e5e7eb",
                                cursor: "pointer",
                              }}
                              preview={{ mask: false }}
                            />
                          ) : (
                            <span style={{ color: "#d1d5db", fontSize: 12 }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Modal>

        {/* ══ Full Work Status Modal ══ */}
        <Modal
          open={isFullStatusModalVisible}
          onCancel={handleFullStatusClose}
          width={480}
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span
                style={{
                  cursor: "pointer",
                  fontSize: 18,
                  color: "#2563eb",
                  lineHeight: 1,
                }}
                onClick={handleFullStatusClose}
              >
                ←
              </span>
              <span style={{ fontWeight: 600, fontSize: 15 }}>
                Full Work Status
              </span>
            </div>
          }
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                className="btn btn-primary"
                onClick={handleFullStatusClose}
              >
                Back
              </button>
            </div>
          }
        >
          <p
            style={{
              fontSize: 14,
              color: "#374151",
              lineHeight: 1.7,
              margin: 0,
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              padding: "14px 16px",
            }}
          >
            {selectedStatus}
          </p>
        </Modal>
      </div>
    </div>
  );
};

export default EmployeeWorkStatusPage;
